import React from 'react';

/**
 * AnimatedBackground — fondos decorativos animados (mesh gradient, fractales).
 *
 * Variantes:
 *  - 'aurora' (DEFAULT NUEVO): mesh gradient cinemático con 4 blobs + conic rotación lenta.
 *  - 'mesh'   : dos/tres blobs cyan/azul que orbitan (variante histórica).
 *  - 'flow'   : líneas fractales SVG con animación de stroke.
 *  - 'soft'   : un halo grande con respiración.
 *
 * Sin pointer-events, sin afectar usabilidad. Usa transform / will-change.
 */
const AnimatedBackground = ({ variant = 'aurora', intensity = 0.35 }) => {
  if (variant === 'flow') {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none -z-0">
        <svg
          className="absolute inset-0 w-full h-full opacity-[0.18]"
          viewBox="0 0 800 600"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <linearGradient id="flowGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00e0c7" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#00a3ff" stopOpacity="0" />
            </linearGradient>
          </defs>
          {[0, 1, 2, 3, 4].map((i) => (
            <path
              key={i}
              d={`M0,${120 + i * 90} C200,${60 + i * 80} 600,${240 + i * 50} 800,${100 + i * 90}`}
              fill="none"
              stroke="url(#flowGrad)"
              strokeWidth="1.2"
              style={{
                animation: `flowDraw 14s ease-in-out ${i * 1.2}s infinite alternate`,
                strokeDasharray: 1400,
              }}
            />
          ))}
        </svg>
      </div>
    );
  }

  if (variant === 'soft') {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none -z-0">
        <div className="absolute left-1/2 top-1/2 w-[900px] h-[900px] rounded-full -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(circle,rgba(0,224,199,0.10),transparent_60%)] animate-breath" />
      </div>
    );
  }

  if (variant === 'mesh') {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none -z-0">
        <div
          className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full blur-[110px] mix-blend-screen animate-blobA"
          style={{ background: 'radial-gradient(circle, rgba(0,224,199,0.45), transparent 65%)', opacity: intensity }}
        />
        <div
          className="absolute top-1/3 right-[-120px] w-[620px] h-[620px] rounded-full blur-[130px] mix-blend-screen animate-blobB"
          style={{ background: 'radial-gradient(circle, rgba(0,163,255,0.35), transparent 65%)', opacity: intensity }}
        />
        <div
          className="absolute bottom-[-160px] left-[20%] w-[540px] h-[540px] rounded-full blur-[120px] mix-blend-screen animate-blobC"
          style={{ background: 'radial-gradient(circle, rgba(94,234,212,0.35), transparent 65%)', opacity: intensity * 0.9 }}
        />
        <div className="absolute inset-0 opacity-[0.035] mix-blend-overlay" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/%3E%3C/svg%3E")',
        }} />
      </div>
    );
  }

  // aurora (DEFAULT) — mesh gradient cinemático, 4 blobs + conic rotando
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none -z-0" aria-hidden="true">
      {/* Conic gradient base, rotando lentamente para efecto mesh */}
      <div
        className="absolute inset-0 animate-meshRotate mix-blend-screen"
        style={{
          background:
            'conic-gradient(from 0deg at 50% 50%, rgba(0,224,199,0.12), rgba(0,163,255,0.08), rgba(94,234,212,0.10), rgba(120,80,255,0.06), rgba(0,224,199,0.12))',
          opacity: intensity * 0.8,
        }}
      />
      {/* 4 blobs orbitando */}
      <div
        className="absolute -top-40 -left-32 w-[600px] h-[600px] rounded-full blur-[120px] mix-blend-screen animate-blobA"
        style={{ background: 'radial-gradient(circle, rgba(0,224,199,0.55), transparent 65%)', opacity: intensity }}
      />
      <div
        className="absolute top-1/4 right-[-160px] w-[680px] h-[680px] rounded-full blur-[140px] mix-blend-screen animate-blobB"
        style={{ background: 'radial-gradient(circle, rgba(0,163,255,0.42), transparent 65%)', opacity: intensity }}
      />
      <div
        className="absolute bottom-[-180px] left-[18%] w-[560px] h-[560px] rounded-full blur-[120px] mix-blend-screen animate-blobC"
        style={{ background: 'radial-gradient(circle, rgba(94,234,212,0.40), transparent 65%)', opacity: intensity * 0.95 }}
      />
      <div
        className="absolute top-[55%] left-[40%] w-[420px] h-[420px] rounded-full blur-[110px] mix-blend-screen animate-blobD"
        style={{ background: 'radial-gradient(circle, rgba(120,80,255,0.28), transparent 65%)', opacity: intensity * 0.7 }}
      />
      {/* Grain sutil para textura cinemática */}
      <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/%3E%3C/svg%3E")',
      }} />
    </div>
  );
};

export default AnimatedBackground;
